import { toRgb } from "./";
// There are multiple standards that describe the thresholds of the sRGB
// transfer function. They differ slightly, and are described below.
//
// [0] https://webstore.iec.ch/publication/6169
// [1] https://en.wikipedia.org/wiki/SRGB#Specification_of_the_transformation
// [2] http://www.color.org/srgb.pdf
// [3] https://www.w3.org/TR/2008/REC-WCAG20-20081211/#relativeluminancedef
// [4] https://www.w3.org/Graphics/Color/sRGB
// [5] http://entropymine.com/imageworsener/srgbformula/
var sRgbValuesByStandard = {
  // The most common thresholds used for the sRGB transfer function.
  // As specified by the IEC in IEC 61966-2-1:1999 [0].
  // Specified in detail by the sRGB Wikipedia entry [1] and the ICC [2].
  iec: {
    slope: 12.92,
    srgb: 0.04045,
    linear: 0.0031308
  },
  // The original thresholds used for the sRGB transfer function.
  //
  // As specified by WCAG [3] which references the original W3 sRGB proposal [4],
  // published in 1996.
  //
  // Unfortunately, the rounding of these thresholds creates a small
  // discontinuity in the sRGB gamma curve [1][5]. The IEC thresholds are
  // techincally more accurate, but some continue to use these definitions.
  wcag: {
    slope: 12.92,
    srgb: 0.03928,
    linear: 0.00304
  },
  // Non-standard.
  //
  // The rounding in the IEC definitions still create a very slight
  // discontinuity in the gamma curve. These unrounded values close the gap.[5]
  preciseIec: {
    slope: 12.92,
    srgb: 0.0404482362771082,
    linear: 0.00313066844250063
  },
  // Non-standard.
  //
  // The rounding in the WCAG definitions create a discontinuity in the gamma
  // curve. These unrounded values close the gap by changing the slope.[1]
  preciseWcag: {
    slope: 12.9232102,
    srgb: 0.0392857,
    linear: 0.00303993
  }
};

var sRgbToLinearRgb = function sRgbToLinearRgb(standard, value) {
  var _sRgbValuesByStandard = sRgbValuesByStandard[standard],
      slope = _sRgbValuesByStandard.slope,
      srgb = _sRgbValuesByStandard.srgb;

  if (value <= srgb) {
    return value / slope;
  } else {
    return Math.pow((value + 0.055) / 1.055, 2.4);
  }
};

export var relativeLuminance = function relativeLuminance(standard, color) {
  var rgb = toRgb(color);
  var r = sRgbToLinearRgb(standard, rgb.r / 255);
  var g = sRgbToLinearRgb(standard, rgb.g / 255);
  var b = sRgbToLinearRgb(standard, rgb.b / 255);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};
export var wcagRelativeLuminance = function wcagRelativeLuminance(color) {
  return relativeLuminance('wcag', color);
}; // https://www.w3.org/TR/WCAG20/Overview.html#contrast-ratiodef

export var wcagContrastRatio = function wcagContrastRatio(a, b) {
  var aL = wcagRelativeLuminance(a);
  var bL = wcagRelativeLuminance(b);
  var minL = Math.min(aL, bL);
  var maxL = Math.max(aL, bL);
  return (maxL + 0.05) / (minL + 0.05);
}; // https://www.w3.org/TR/WCAG20/Overview.html#visual-audio-contrast-contrast

export var wcagContrastRatioThreshold = {
  AALarge: 3,
  AASmall: 4.5,
  AAALarge: 4.5,
  AAASmall: 7
};
export var contrastRatioValidator = function contrastRatioValidator(_ref) {
  var fontSize = _ref.fontSize,
      _ref$weight = _ref.weight,
      weight = _ref$weight === void 0 ? 'regular' : _ref$weight,
      _ref$level = _ref.level,
      level = _ref$level === void 0 ? 'AA' : _ref$level;
  return function (a, b) {
    var ratio = wcagContrastRatio(a, b);
    var AALarge = wcagContrastRatioThreshold.AALarge,
        AASmall = wcagContrastRatioThreshold.AASmall,
        AAALarge = wcagContrastRatioThreshold.AAALarge,
        AAASmall = wcagContrastRatioThreshold.AAASmall;
    var isAAA = level === 'AAA';
    var isBold = weight === 'medium' || weight === 'bold' || typeof weight === 'number' && weight >= 500;
    var largeFontSizeThreshold = isBold ? 14 : 18;
    var ratioThreshold = isAAA ? AAASmall : AASmall;

    if (fontSize >= largeFontSizeThreshold) {
      ratioThreshold = isAAA ? AAALarge : AALarge;
    }

    return ratio >= ratioThreshold;
  };
};